// Shared pieces of the Vitals (Sentry-protocol) config, imported by
// instrumentation-client.ts, sentry.server.config.ts and sentry.edge.config.ts.
// One module on purpose — the DSN resolution, the common init options and the
// PII scrub must be identical across the three inits, and three hand-kept
// copies is how the next PII rule lands in one file and not the others (it
// happened twice before this module absorbed them). Same reasoning as
// scripts/vitals-release.cjs for the release string.
//
// This file must stay importable from BOTH the browser bundle and the node/edge
// runtimes: no React, no node builtins, no side effects.

// Project 9, created 2026-09-01. A DSN is a public key plus a project id: it is
// designed to live in a browser bundle and can only WRITE events, never read
// them. Keeping it in source means no deploy-time env var and no way for a
// missing Vercel setting to silently disable reporting.
//
// NOTE the host. `app.cli create-project` prints vitals.ultrahuman.com, which
// is VPN-ONLY — this is rewritten to the public ingest host on purpose. Pointed
// at the internal one, every report from a real visitor fails silently and the
// site looks error-free.
export const DEFAULT_DSN =
  'https://f528df7b466b436090419ece31fd2f6c@vitals-ingest.ultrahuman.com/9';

/**
 * Explicit "" disables reporting; unset falls back to the built-in DSN.
 *
 * Callers pass their own env lookup because the browser can only see
 * NEXT_PUBLIC_* values: the client passes NEXT_PUBLIC_VITALS_DSN, the server
 * and edge pass VITALS_DSN ?? NEXT_PUBLIC_VITALS_DSN. Consequence worth
 * knowing when rotating DSNs: setting only the server-side VITALS_DSN moves
 * the server while every browser keeps posting to the old DSN — set the
 * NEXT_PUBLIC one (and redeploy) to move the client too.
 */
export function resolveDsn(configured: string | undefined): string {
  return configured === undefined ? DEFAULT_DSN : configured;
}

/** Server/edge DSN resolution, shared so the precedence and the loud disable
 *  warning cannot drift between the two runtimes. */
/**
 * The single gate for "may this runtime send events to Vitals at all",
 * shared by the client, server and edge inits so the ingestion boundary
 * cannot drift between them.
 *
 * Keyed on the DEPLOY environment, deliberately NOT on NODE_ENV. NODE_ENV is
 * 'production' for a local `yarn build && yarn start` too, so gating on it
 * shipped developers' laptop errors into the shared store — and, because
 * Vitals alerts on every new fault by default, into Slack. VERCEL_ENV is set
 * by Vercel itself and cannot be forged by a local .env, which is what makes
 * 'local' a trustworthy sentinel for "not a real deploy".
 *
 * VITALS_FORCE_REPORT=1 is the deliberate opt-in for exercising the real
 * pipeline from a local build. Events still carry environment: 'local', so
 * they stay filterable and can never trip a production alert. One-shot use —
 * never export it from a shell profile.
 */
export function shouldReportToVitals(): boolean {
  if (process.env.NEXT_PUBLIC_VITALS_FORCE_REPORT === '1') {
    // LOUD: this path sends laptop errors into the shared store on purpose.
    // It must never be the state someone is in without knowing it.
    console.warn(
      '[vitals] VITALS_FORCE_REPORT=1 — this build REPORTS to Vitals, tagged ' +
        "environment: 'local'. Unset it for normal local work.",
    );
    return true;
  }
  // ALLOWLIST, not a denylist. VERCEL_ENV has a third value beyond the two
  // real deploy kinds: 'development', set by `vercel dev` and written into
  // .env.local by `vercel env pull` when system env vars are exposed. A
  // `!== 'local'` test would let that through and report from a laptop —
  // exactly the bug this gate exists to close. Anything unrecognised, unset,
  // or misspelled therefore fails CLOSED and sends nothing.
  return (
    process.env.NEXT_PUBLIC_VITALS_DEPLOY_ENV === 'production' ||
    process.env.NEXT_PUBLIC_VITALS_DEPLOY_ENV === 'preview'
  );
}

export function resolveServerDsn(): string {
  const dsn = resolveDsn(
    process.env.VITALS_DSN ?? process.env.NEXT_PUBLIC_VITALS_DSN,
  );
  if (!dsn && shouldReportToVitals()) {
    // LOUD, not silent — same principle as the client-side warning. An ops
    // engineer setting VITALS_DSN="" during an incident must leave a trace
    // saying server reporting is off.
    console.error(
      '[vitals] server-side reporting disabled via VITALS_DSN="" — server ' +
        'errors are NOT being reported.',
    );
  }
  return dsn;
}

/**
 * The init options shared by all three runtimes. A builder (not a constant) so
 * each init evaluates env at its own load time; spread it and add
 * runtime-specific options after.
 */
export function baseVitalsOptions() {
  return {
    // Deploy env ('production' | 'preview' | 'local'), inlined at build time
    // from VERCEL_ENV via next.config.js — NOT NEXT_PUBLIC_ENVIRONMENT, which
    // local machines also run as PRODUCTION. Feature env kept as a tag.
    environment: process.env.NEXT_PUBLIC_VITALS_DEPLOY_ENV || 'local',
    initialScope: {
      tags: { app_env: process.env.NEXT_PUBLIC_ENVIRONMENT || 'unknown' },
    },
    // Must be byte-identical to what scripts/upload-sourcemaps.mjs keys maps
    // by, or no frame is ever symbolicated. Both read scripts/vitals-release.cjs.
    release: process.env.NEXT_PUBLIC_VITALS_RELEASE || undefined,
    // NO tracesSampleRate key, not even 0: in SDK v10, hasSpansEnabled()
    // checks non-nullish, so 0 still enables the span machinery and then
    // samples everything away. Omitting the key is what disables tracing.
    sendDefaultPii: false,
    debug: false,
    beforeSend: scrubEvent,
    // MUST live here, not in the client init only: consoleIntegration() is a
    // DEFAULT integration on the Node SDK too, so a server or edge runtime
    // records console arguments verbatim exactly like the browser does. Wiring
    // this on the client alone left API routes shipping raw upstream payloads
    // into the shared store — the precise disclosure this exists to prevent.
    beforeBreadcrumb: trimConsoleBreadcrumb,
  } as const;
}

/** Static route segments directly under /order/ — i.e. the directory names in
 *  `app/(lite)/order/` that are not dynamic. KEEP IN SYNC with that directory:
 *  a product missing from this list still cannot leak a token (the orderNo rule
 *  below consumes the remainder of the path), it just reports as
 *  /order/[token]. */
const ORDER_PRODUCT_SEGMENTS = 'accessories|blood-vision|home|m1|ring|ring-pro';

/** Static CHILDREN of a product segment — pages, not capability tokens. Every
 *  other child position is dynamic (`[token]`, `[orderNo]`, `[orderId]`). */
const ORDER_STATIC_CHILDREN = 'blood-on-us';

/** The segment after an /api/web_v1/<endpoint>/ that is a real sub-path rather
 *  than an identifier: region suffixes, a provider name, and the science
 *  collections. Anything else in that position is treated as a token. */
const API_STATIC_CHILDREN =
  'ae|in|shopify|accuracy_studies|explainers|publications|white_papers';

/**
 * Some of this site's URLs carry live bearer capability tokens as PATH
 * segments, not query params: /cycle-report/<token> alone fetches the user's
 * full health/cycle report from the API with no other auth, and
 * /order/<product>/<token> (or /order/<orderNo>) returns order PII. Those must
 * never reach Vitals — the dashboard audience is wider than the set of people
 * authorized to see customer health data. Keeps the route shape (which page
 * broke), redacts the capability.
 */
function redactTokenPathSegments(path: string): string {
  return (
    path
      // Site route: /cycle-report/<token>
      .replace(/(\/cycle-report\/)[^/?#]+/, '$1[token]')
      // Site route: /order/<product>/<token>. The product segment is a real
      // route name worth keeping (it says which checkout broke); the segment
      // after it is the capability token — EXCEPT for the static children
      // listed here, which are pages, not tokens.
      .replace(
        new RegExp(
          `(/order/(?:${ORDER_PRODUCT_SEGMENTS})/)(?!(?:${ORDER_STATIC_CHILDREN})(?:[/?#]|$))[^/?#]+`,
          'i',
        ),
        '$1[token]',
      )
      // Site route: /order/<orderNo>. Anything in the product position that is
      // NOT a known product is the [orderNo] dynamic segment, which is order
      // PII. Consumes the REST of the path (`[^?#]+`, not `[^/?#]+$`) rather
      // than one segment, so this fails CLOSED: if a new product route is added
      // under /order/ and nobody updates the list above, the worst case is a
      // route shape collapsed to /order/[token] — never a surviving token.
      // `$`-anchoring instead would leak outright, because trailingSlash:true
      // (next.config.js) means the live URL is /us/order/ORD12345/, with the
      // order number NOT at the end of the string.
      .replace(
        new RegExp(
          `(/order/)(?!(?:${ORDER_PRODUCT_SEGMENTS})(?:[/?#]|$))[^?#]+`,
          'i',
        ),
        '$1[token]',
      )
      // API shapes the same tokens travel through (fetch breadcrumbs,
      // safeFetch extra.url): .../cycle_report/<token>.html
      .replace(/(\/cycle_report\/)[^/?#]+/, '$1[token]')
      // Every /api/web_v1/<endpoint>/<next> where <next> is NOT one of the
      // known static sub-paths. Enumerating the token-carrying endpoints
      // instead was the previous approach and it had already rotted: it caught
      // the *_order_status reads but missed update_ring_color,
      // update_ring_color_and_size, update_order_shipping_address,
      // ring_size_submission and ring_pro_preorder_confirmation — the same
      // order token, except those are WRITE capabilities. Inverting it makes a
      // newly added endpoint redacted by default; the cost of a missing entry
      // is an over-redacted static path, not a live token in the dashboard.
      .replace(
        new RegExp(
          `(/api/web_v1/[a-z_]+/)(?!(?:${API_STATIC_CHILDREN})(?:[/?#]|$))[^/?#]+`,
          'i',
        ),
        '$1[token]',
      )
  );
}

/** Query strings carry UTM params, click ids, prefill emails; fragments can
 *  carry tokens; some PATHS carry bearer tokens (see above). The route says
 *  which page broke, which is the part worth keeping. Exported so other
 *  reporters apply the same rules. NOTE: the only other consumer,
 *  safeFetch, was moved to legacy/utils/safeFetch.ts by main's cleanup and is
 *  excluded from typecheck/lint/gates there — so a rename here will NOT be
 *  caught against it. See docs/vitals-error-reporting.md. */
export function sanitizeReportedUrl(url: unknown): unknown {
  return typeof url === 'string'
    ? redactTokenPathSegments(url.split(/[?#]/)[0])
    : url;
}

/**
 * Masks the health TOPIC while keeping the rest of the path.
 *
 * A SINGLE event naming a health page is inherent to reporting which page
 * broke. An ordered BREADCRUMB TRAIL across several is different in kind: a
 * browsing history over reproductive-health and lab-test topics, which under
 * CJEU C-252/21 is "liable to reveal" health data (GDPR Art 9). Art 9 has no
 * legitimate-interest gateway, so the control has to be technical.
 *
 * Two replacements on purpose. Page routes use hyphens and terminate at a
 * segment boundary, so /womens-health-guide (a different page) is untouched.
 * API paths use underscores and extend the segment
 * (/api/web_v1/blood_vision_free_request), so they take `_` as a terminator.
 *
 * The sensitive part is the subject — "blood vision" means lab tests, "cycle
 * report" means reproductive health. The rest of the path is not:
 * `serviceability_status` reveals nothing on its own. Replacing the whole URL
 * with one marker (the first attempt) protected the topic but also collapsed
 * the blood-vision checkout funnel into three identical entries, so a failing
 * call could not be told from a succeeding one on a revenue path.
 *
 * NOTE this runs on BREADCRUMBS ONLY. The event's own request.url and
 * contexts.nextjs.request_path keep the real route, so the page that actually
 * broke is always identifiable — what this prevents is accumulating an ordered
 * trail of health TOPICS alongside it.
 */
function maskHealthTopic(url: unknown): unknown {
  if (typeof url !== 'string') return url;
  // Lookahead, not a consuming group, and global. A consuming `(\/|$)` eats the
  // separating slash, so the next candidate loses its leading `/` and survives:
  // /blood-vision/cycle-report/x would mask only the first topic.
  return url
    .replace(
      /\/(cycle-report|blood-vision|womens-health)(?=\/|$)/gi,
      '/[health]',
    )
    .replace(/\/(cycle_report|blood_vision)(?=[_/]|$)/gi, '/[health]');
}

type ExceptionValue = {
  type?: string;
  value?: string;
  mechanism?: { handled?: boolean };
  stacktrace?: { frames?: Array<{ filename?: string }> };
};

type ScrubbableEvent = {
  request?: {
    url?: string;
    query_string?: unknown;
    data?: unknown;
    cookies?: unknown;
    headers?: unknown;
  };
  breadcrumbs?: Array<{
    category?: string;
    message?: string;
    data?: Record<string, unknown>;
  }>;
  contexts?: { nextjs?: { request_path?: unknown } } & Record<string, unknown>;
  exception?: { values?: ExceptionValue[] };
};

/**
 * The shared PII scrub, used as `beforeSend` by all three inits.
 *
 * - request.url loses its query string, fragment, and token path segments.
 * - request.query_string is DELETED: the node SDK populates it as a separate
 *   field even with sendDefaultPii: false (verified in @sentry/core: the
 *   urlQueryParams deny-list only filters the span path, not the event path),
 *   so stripping request.url alone leaves the same payload one field over.
 *   request.data likewise, belt-and-braces (bodies are not captured today).
 * - cookies and the whole header set are dropped: server events can carry auth
 *   headers; browser events carry document.referrer as a header, which is
 *   usually the PREVIOUS page's URL with the same UTM payload.
 * - contexts.nextjs.request_path is sanitized: Next passes req.url INCLUDING
 *   the query string to onRequestError and captureRequestError stores it
 *   verbatim — a scrub bypass unless handled here.
 * - breadcrumb url/from/to fields are sanitized the same way.
 */
export function scrubEvent<T extends ScrubbableEvent>(event: T): T {
  if (event.request?.url) {
    event.request.url = sanitizeReportedUrl(event.request.url) as string;
  }
  if (event.request) {
    delete event.request.cookies;
    delete event.request.headers;
    delete event.request.query_string;
    delete event.request.data;
  }
  if (event.contexts?.nextjs?.request_path) {
    event.contexts.nextjs.request_path = sanitizeReportedUrl(
      event.contexts.nextjs.request_path,
    );
  }
  // Stack-frame filenames. Normally a chunk URL (untouched by the scrub), but
  // the browser SDK falls back to the DOCUMENT url for any error it cannot
  // parse a frame from — cross-origin "Script error." and throws inside inline
  // <script> blocks. The layout no longer injects New Relic, GTM, or gtag.
  // A throw in an inline script while the visitor is on
  // /cycle-report/<token>/ would otherwise store the live
  // bearer token in a field nothing else here inspects.
  for (const value of event.exception?.values ?? []) {
    for (const frame of value.stacktrace?.frames ?? []) {
      if (frame.filename) {
        frame.filename = sanitizeReportedUrl(frame.filename) as string;
      }
    }
  }
  event.breadcrumbs = event.breadcrumbs?.map((crumb) => {
    // ui.click / ui.input breadcrumbs carry an HTML-tree description built from
    // the element's attributes, INCLUDING aria-label and title. Those are
    // developer-written strings and interpolating visitor data into them is
    // ordinary accessibility practice, so the value is unpredictable by
    // construction. The element path is not worth a PII surface with no gate on
    // it, so the message is dropped and the selector kept.
    if (crumb.category?.startsWith('ui.')) {
      return { ...crumb, message: undefined };
    }
    return crumb.data
      ? {
          ...crumb,
          data: {
            ...crumb.data,
            url: maskHealthTopic(sanitizeReportedUrl(crumb.data.url)),
            from: maskHealthTopic(sanitizeReportedUrl(crumb.data.from)),
            to: maskHealthTopic(sanitizeReportedUrl(crumb.data.to)),
          },
        }
      : crumb;
  });
  return event;
}

// Consumer-connection noise that would drown real errors — but ONLY when
// unhandled. These used to live in ignoreErrors, which was wrong: verified in
// @sentry/core's eventFilters, ignoreErrors drops ALL matching events with no
// mechanism.handled check, so it also swallowed deliberate captureException
// calls (legacy/utils/safeFetch.ts — quarantined, zero importers) whose
// messages contain the same strings.
// 'Load failed' is Safari's message for EVERY fetch TypeError, and safeFetch's
// timeout aborts surface as 'AbortError: timeout' — both must remain
// reportable when explicitly captured with context.
const UNHANDLED_NETWORK_FLAKE = [
  /Failed to fetch/i,
  /NetworkError when attempting to fetch resource/i,
  /Load failed/i,
  /AbortError/i,
];

/**
 * Console breadcrumbs: keep the LABEL, drop the PAYLOAD.
 *
 * The SDK records console calls verbatim — `data.arguments` holds the raw
 * arguments and `message` is every argument stringified and joined. This repo
 * logs the thing that failed, so `console.error('cart update failed', body)`
 * would carry a customer's order into a 30-day store readable by everyone with
 * dashboard access.
 *
 * Dropping console breadcrumbs entirely (the first attempt) threw away real
 * debugging value to solve that. Keeping only the first argument — which is
 * the static string in essentially every call site — preserves "a console
 * error happened, and here is what it was called" while the interpolated data
 * never leaves the browser.
 *
 * Note the asymmetry with the console itself, which is deliberate: a visitor
 * seeing their own data in their own devtools is not a disclosure, whereas
 * copying it into a shared internal store is a different audience entirely.
 */
export function trimConsoleBreadcrumb<
  T extends {
    category?: string;
    message?: string;
    data?: Record<string, unknown>;
  },
>(crumb: T): T {
  if (crumb.category !== 'console') return crumb;
  // Array.isArray, not `?? []`: destructuring a non-iterable throws, and a
  // handler that throws loses the breadcrumb silently.
  const args = crumb.data?.arguments;
  const [first] = Array.isArray(args) ? (args as unknown[]) : [];
  // `console.error(err)` is the dominant shape in this repo's catch blocks (19
  // call sites pass a non-string first argument, including both root error
  // boundaries and the ring/m1 selection contexts). Stringifying just the
  // name and message keeps the one useful line without serialising an object
  // graph whose fields nothing here has vetted.
  const label =
    typeof first === 'string'
      ? first
      : first instanceof Error
      ? `${first.name}: ${first.message}`
      : '(non-string console argument)';
  return {
    ...crumb,
    // sanitize the LABEL too. It is a free-text string a developer wrote, and
    // interpolating a URL into it is ordinary — `console.error(\`failed for
    // ${url}\`)`. Without this, a /cycle-report/<token> address in a log line
    // would carry a live bearer credential straight past redactTokenPathSegments,
    // which only ever sees crumb.data.url/from/to.
    // maskHealthTopic as well as the token redaction: this is a breadcrumb, and
    // the health-topic trail argument applies to it exactly as it does to
    // crumb.data.url. Without it `console.error(`failed for ${url}`)` would
    // contribute an ordered trail of health topics that scrubEvent's own
    // breadcrumb pass masks everywhere else.
    message: String(maskHealthTopic(sanitizeReportedUrl(label.slice(0, 200)))),
    data: { logger: crumb.data?.logger, arguments_omitted: true },
  };
}

/** True when the event is an UNHANDLED error matching the network-flake list.
 *  Explicit captures (mechanism.handled === true) always pass. */
export function isUnhandledNetworkFlake(event: ScrubbableEvent): boolean {
  // LAST, not first: linkedErrorsIntegration (a default on both runtimes)
  // PREPENDS causes, so values[0] is the cause of a chained error, not the
  // error thrown. Sentry's own getPossibleEventMessages reads the last entry
  // for the same reason.
  const values = event.exception?.values;
  const primary = values?.[values.length - 1];
  if (!primary || primary.mechanism?.handled === true) return false;
  const title = `${primary.type ?? ''}: ${primary.value ?? ''}`;
  return UNHANDLED_NETWORK_FLAKE.some((re) => re.test(title));
}

// Safari extensions mask their origin as webkit-masked-url://hidden/, HubSpot
// pixels.js JSON.parses CDN HTML, Unicorn Studio throws when WebGL is missing,
// and WebKit img.decode() rejects as EncodingError. None of these are ours.
const THIRD_PARTY_MESSAGES = [
  /contentScriptData\.init_ts/,
  /window\.webkit\.messageHandlers/,
  /remoteConfig\.apiKey/,
  /Invalid image request/,
  /ChunkLoadError/,
  /Failed to load chunk /i,
  /Plane not properly initialized/,
];

const THIRD_PARTY_FRAMES = [
  /webkit-masked-url:/i,
  /pixels\.js/i,
  /hubapi\.com/i,
  /js\.hs-scripts\.com/i,
  /unicornStudio\.umd\.js/i,
];

/** True when the event is browser-extension / third-party-tag / deploy-skew
 *  noise. Dropped regardless of mechanism.handled — we never captureException
 *  these on purpose. */
export function isThirdPartyBrowserNoise(event: ScrubbableEvent): boolean {
  const values = event.exception?.values;
  const primary = values?.[values.length - 1];
  if (!primary) return false;
  const title = `${primary.type ?? ''}: ${primary.value ?? ''}`;
  if (THIRD_PARTY_MESSAGES.some((re) => re.test(title))) return true;
  const frames = values?.flatMap((v) => v.stacktrace?.frames ?? []) ?? [];
  return frames.some((f) =>
    THIRD_PARTY_FRAMES.some((re) => re.test(f.filename ?? '')),
  );
}
